import { MAX_CART_ITEMS } from "@/lib/whatsapp";

export const DEFAULT_CART_ITEM_QUANTITY = 1;
export const MAX_CART_ITEM_QUANTITY = 99;

export interface CartItem {
  productId: string;
  name: string;
  slug: string;
  priceCents: number;
  quantity: number;
  thumbnailUrl?: string | null;
}

const STORAGE_PREFIX = "whatsapp-store:cart:";

function storageKey(storeSlug: string): string {
  return `${STORAGE_PREFIX}${storeSlug}`;
}

function isCartItem(value: unknown): value is CartItem {
  if (!value || typeof value !== "object") return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item.productId === "string" &&
    typeof item.name === "string" &&
    typeof item.slug === "string" &&
    typeof item.priceCents === "number" &&
    (item.quantity === undefined ||
      (typeof item.quantity === "number" && item.quantity >= 1)) &&
    (item.thumbnailUrl === undefined ||
      item.thumbnailUrl === null ||
      typeof item.thumbnailUrl === "string")
  );
}

export function normalizeCartItem(item: CartItem): CartItem {
  const quantity =
    item.quantity == null
      ? DEFAULT_CART_ITEM_QUANTITY
      : Math.min(MAX_CART_ITEM_QUANTITY, Math.max(1, Math.floor(item.quantity)));

  return { ...item, quantity };
}

export function loadCart(storeSlug: string): CartItem[] {
  try {
    const raw = localStorage.getItem(storageKey(storeSlug));
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter(isCartItem).map(normalizeCartItem).slice(0, MAX_CART_ITEMS);
  } catch {
    return [];
  }
}

export function saveCart(storeSlug: string, items: CartItem[]): void {
  try {
    const key = storageKey(storeSlug);
    if (items.length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(items));
    }
  } catch {
    // Quota exceeded or private browsing — cart stays in memory only
  }
}
